import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { Props as ButtonProps } from '@sorare/core/src/atoms/buttons/Button';
import { SocialShare } from '@sorare/core/src/components/user/SocialShare';
import { ANY_SPORT_MARKET_WATCHLISTS_ID } from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useAbsoluteURL } from '@sorare/core/src/hooks/useAbsoluteURL';

import type { ShareWatchlistButton_watchlist } from './__generated__/index.graphql.ts';

type Props = {
  watchlist: ShareWatchlistButton_watchlist;
} & ButtonProps;

export const ShareWatchlistButton = ({
  watchlist,
  onClick,
  ...props
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { formatMessage } = useIntlContext();
  const shareUrl = useAbsoluteURL(
    generateSportContextPath(ANY_SPORT_MARKET_WATCHLISTS_ID, {
      params: { id: idFromObject(watchlist.id) },
    })
  );
  return (
    <SocialShare
      url={shareUrl}
      title={formatMessage(
        {
          id: 'ShareWatchlistButton.title',
          defaultMessage: 'Share "{name}" watchlist',
        },
        { name: watchlist.title }
      )}
      message={formatMessage(
        {
          id: 'ShareWatchlistButton.message',
          defaultMessage: 'Check out my watchlist! “{name}”',
        },
        { name: watchlist.title }
      )}
      trackingEventName="Share Watchlist"
      trackingEventContext="Watchlist Page"
      renderButton={({ ShareButton, Icon, label }) => (
        <ShareButton startIcon={Icon} {...props}>
          {label}
        </ShareButton>
      )}
    />
  );
};

ShareWatchlistButton.fragments = {
  watchlist: gql`
    fragment ShareWatchlistButton_watchlist on Watchlist {
      slug
      id
      title
    }
  ` as TypedDocumentNode<ShareWatchlistButton_watchlist>,
};
